import React, { useState, useEffect, useRef, useCallback } from 'react';
import { demarcheAPI, requirementAPI } from '../../service/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { resolveUploadUrl } from '../../config/config';
import { promptReason, toast } from '../../service/swal';
import {
  FiFileText, FiCheckCircle, FiClock, FiXCircle, FiPlus,
  FiUpload, FiEye, FiDownload, FiPaperclip, FiChevronDown,
  FiChevronUp, FiUser, FiCalendar
} from 'react-icons/fi';
import PageMeta from '../../components/PageMeta';
import useAutoRefresh from '../../hooks/useAutoRefresh';

const stepLabels = {
  visa: 'Visa étudiant', academic: 'Inscription académique',
  insurance: 'Assurance', residence_permit: 'Carte de séjour',
};
const stepIcons = { visa: '🛂', academic: '📚', insurance: '🛡️', residence_permit: '🪪' };

const statusConfig = {
  en_attente: { icon: FiClock, label: 'EN ATTENTE', color: '#D4A017', bg: '#FFF8E0', border: '#D4A017' },
  en_cours: { icon: FiFileText, label: 'EN COURS', color: '#1565C0', bg: '#E3F0FF', border: '#1565C0' },
  valide: { icon: FiCheckCircle, label: 'VALIDÉ', color: '#2E7D32', bg: '#E8F5E9', border: '#2E7D32' },
  rejete: { icon: FiXCircle, label: 'REJETÉ', color: '#C62828', bg: '#FFEBEE', border: '#C62828' },
};

const fileIcons = {
  pdf: '📄', jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️',
  doc: '📝', docx: '📝', xls: '📊', xlsx: '📊',
};

function getFileIcon(filename) {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return fileIcons[ext] || '📎';
}

export default function DemarcheSuivi() {
  const { user } = useAuth();
  const { socket } = useSocket();
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedType, setSelectedType] = useState('visa');
  const [expandedStep, setExpandedStep] = useState(null);
  const [stepDetail, setStepDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [requirements, setRequirements] = useState({});
  const fileInputRef = useRef(null);

  const fetchSteps = useCallback(async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try { const r = await demarcheAPI.getAll(); setSteps(r.data.data); }
    catch (err) { console.error(err); }
    finally { if (!skipLoading) setLoading(false); }
  }, []);
  useEffect(() => { fetchSteps(); }, [fetchSteps]);
  useAutoRefresh(() => fetchSteps(true), 30000);

  useEffect(() => {
    if (!socket) return;
    const handleUpdate = (data) => {
      const labelMap = { visa: 'Visa', academic: 'Académique', insurance: 'Assurance', residence_permit: 'Séjour' };
      toast(`${labelMap[data.type] || data.type} : ${data.statut}`, 'info');
      fetchSteps();
      if (expandedStep && data.stepId === expandedStep) {
        toggleExpand(data.stepId, data.type);
      }
    };
    socket.on('status_update', handleUpdate);
    return () => socket.off('status_update', handleUpdate);
  }, [socket, expandedStep]);

  const handleCreate = async () => {
    try { await demarcheAPI.create({ type: selectedType }); setShowCreate(false); fetchSteps(); }
    catch (err) { console.error(err); }
  };

  const toggleExpand = async (id, stepType) => {
    if (expandedStep === id) { setExpandedStep(null); setStepDetail(null); return; }
    setExpandedStep(id);
    setDetailLoading(true);
    try {
      const [detailRes, reqRes] = await Promise.all([
        demarcheAPI.getById(id),
        requirements[stepType] ? Promise.resolve(null) : requirementAPI.getByType(stepType),
      ]);
      setStepDetail(detailRes.data.data);
      if (reqRes) {
        setRequirements((prev) => ({ ...prev, [stepType]: reqRes.data.data }));
      }
    } catch (err) { console.error(err); }
    finally { setDetailLoading(false); }
  };

  const handleFileUpload = async (stepId, files) => {
    const formData = new FormData();
    for (const f of files) formData.append('fichiers', f);
    try {
      await demarcheAPI.uploadDocument(stepId, formData);
      toggleExpand(stepId);
      fetchSteps();
    } catch (err) { toast('error', 'Erreur lors de l\'upload'); }
  };

  const isServiceOrAdmin = user?.role === 'service_acad' || user?.role === 'admin';

  const statutBar = (step) => {
    const order = ['en_attente', 'en_cours', 'valide'];
    const idx = order.indexOf(step.statutCourant);
    const isRejete = step.statutCourant === 'rejete';
    return (
      <div className="flex items-center gap-1 mt-3">
        {order.map((s, i) => {
          const done = i <= idx && !isRejete;
          const active = i === idx && !isRejete;
          return (
            <React.Fragment key={s}>
              <div className={`h-2 flex-1 ${i === 0 ? 'rounded-l-none' : ''} ${i === order.length - 1 ? 'rounded-r-none' : ''}`}
                style={{ backgroundColor: done ? '#2E7D32' : '#E5E5E0', opacity: active ? 1 : done ? 0.8 : 0.4 }} />
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div className="page-container py-8">
      <PageMeta title="Suivi des démarches" />
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight" style={{ color: '#0A192F' }}>Mes démarches</h1>
          <p className="text-sm mt-1" style={{ color: '#7A828A' }}>Suivez l'avancement de vos démarches administratives</p>
        </div>
        {user?.role === 'student' && (
          <button className="btn-primary-root" onClick={() => setShowCreate(!showCreate)}>
            <FiPlus className="mr-2" /> Nouvelle démarche
          </button>
        )}
      </div>

      {showCreate && (
        <div className="card-root p-6 mb-8">
          <h3 className="font-serif font-bold text-base mb-4" style={{ color: '#0A192F' }}>Lancer une nouvelle démarche</h3>
          <div className="flex flex-col sm:flex-row gap-4">
            <select className="input-root flex-1" value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
              {Object.entries(stepLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button className="btn-primary-root" onClick={handleCreate}>Créer la démarche</button>
            <button className="btn-outline-root" onClick={() => setShowCreate(false)}>Annuler</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2" style={{ borderColor: '#C59B6C', borderTopColor: 'transparent' }} />
        </div>
      ) : steps.length === 0 ? (
        <div className="text-center py-20">
          <FiFileText className="w-16 h-16 mx-auto mb-4" style={{ color: '#E5E5E0' }} />
          <p style={{ color: '#7A828A' }}>Aucune démarche pour le moment</p>
        </div>
      ) : (
        <div className="space-y-4">
          {steps.map((step) => {
            const cfg = statusConfig[step.statutCourant] || statusConfig.en_attente;
            const Icon = cfg.icon;
            const isExpanded = expandedStep === step.id;
            return (
              <div key={step.id} className="card-root">
                <button onClick={() => toggleExpand(step.id, step.type)}
                  className="w-full text-left p-6 flex items-start justify-between hover:bg-[#FAFAF6] transition-colors">
                  <div className="flex items-start gap-4 flex-1">
                    <span className="text-3xl">{stepIcons[step.type] || '📄'}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-serif font-bold text-lg" style={{ color: '#0A192F' }}>
                          {stepLabels[step.type] || step.type}
                        </h3>
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold tracking-widest uppercase px-3 py-1 border-2"
                          style={{ color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}>
                          <Icon className="w-3 h-3" /> {cfg.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs" style={{ color: '#7A828A' }}>
                        <span>Créée le {new Date(step.dateCreation).toLocaleDateString()}</span>
                        {step.documentCount > 0 && <span>{step.documentCount} document(s)</span>}
                      </div>
                      {statutBar(step)}
                    </div>
                  </div>
                  <div style={{ color: '#7A828A' }}>
                    {isExpanded ? <FiChevronUp className="w-5 h-5" /> : <FiChevronDown className="w-5 h-5" />}
                  </div>
                </button>

                {isExpanded && (
                  <div style={{ borderTop: '1px solid #E5E5E0' }}>
                    {detailLoading ? (
                      <div className="p-6 text-center text-sm" style={{ color: '#7A828A' }}>Chargement...</div>
                    ) : stepDetail && stepDetail.id === step.id ? (
                      <div className="p-6 space-y-6">
                        {/* Requirements */}
                        {requirements[step.type]?.length > 0 && (
                          <div>
                            <h4 className="text-[11px] font-bold tracking-widest uppercase mb-3 flex items-center gap-2" style={{ color: '#7A828A' }}>
                              <FiFileText className="w-4 h-4" /> Documents attendus
                            </h4>
                            <div className="space-y-2 mb-6">
                              {requirements[step.type].map((req) => (
                                <div key={req.id} className="p-3 flex items-start gap-3"
                                  style={{ border: '1px solid #E5E5E0', borderLeft: '3px solid', borderLeftColor: req.obligatoire ? '#C59B6C' : '#88929B' }}>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-semibold" style={{ color: '#0A192F' }}>{req.titre}</span>
                                      {req.obligatoire && (
                                        <span className="text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5"
                                          style={{ backgroundColor: '#C59B6C', color: '#0A192F' }}>Obligatoire</span>
                                      )}
                                    </div>
                                    <p className="text-xs mt-1" style={{ color: '#5A626A' }}>{req.description}</p>
                                    <p className="text-xs mt-1" style={{ color: '#88929B' }}>
                                      Formats acceptés : <span className="font-medium">{req.formats}</span>
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Documents */}
                        <div>
                          <h4 className="text-[11px] font-bold tracking-widest uppercase mb-3 flex items-center gap-2" style={{ color: '#7A828A' }}>
                            <FiPaperclip className="w-4 h-4" /> Documents ({stepDetail.documents?.length || 0})
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {(stepDetail.documents || []).map((doc) => {
                              const isImage = /\.(jpg|jpeg|png|gif)$/i.test(doc.fichierUrl);
                              return (
                                  <div key={doc.id} className="p-3" style={{ border: '2px solid #E5E5E0', backgroundColor: '#FAFAF6' }}>
                                    <div className="flex items-start gap-3">
                                      <span className="text-2xl">{isImage ? '🖼️' : '📄'}</span>
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold truncate" style={{ color: '#0A192F' }}
                                          title={doc.nomFichier}>{doc.nomFichier || 'Sans nom'}</p>
                                        <span className={`inline-block text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 mt-1 ${doc.typeDocument === 'document_admin' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                                          {doc.typeDocument === 'document_admin' ? 'Administration' : 'Étudiant'}
                                        </span>
                                        <p className="text-xs mt-1" style={{ color: '#7A828A' }}>
                                          {new Date(doc.dateTelechargement).toLocaleDateString()}
                                      </p>
                                      <div className="flex gap-2 mt-2">
                                        <a href={resolveUploadUrl(doc.fichierUrl)} target="_blank" rel="noopener noreferrer"
                                          className="btn-root text-xs px-3 py-1" style={{ borderColor: '#0A192F', color: '#0A192F', fontSize: '10px' }}>
                                          <FiEye className="w-3 h-3" /> Voir
                                        </a>
                                        <a href={resolveUploadUrl(doc.fichierUrl)} download
                                          className="btn-root text-xs px-3 py-1" style={{ borderColor: '#0A192F', color: '#0A192F', fontSize: '10px' }}>
                                          <FiDownload className="w-3 h-3" /> Télécharger
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {user?.role === 'student' && stepDetail.statutCourant !== 'valide' && (
                            <div className="mt-4">
                              <input type="file" multiple ref={fileInputRef}
                                onChange={(e) => { if (e.target.files.length) handleFileUpload(step.id, e.target.files); }}
                                className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                              <button className="btn-outline-root" onClick={() => fileInputRef.current?.click()}>
                                <FiUpload className="mr-2" /> Ajouter des documents
                              </button>
                            </div>
                          )}

                          {(isServiceOrAdmin || stepDetail.statutCourant !== 'valide') && (
                            <div className="flex flex-wrap gap-3 pt-2">
                              {isServiceOrAdmin && ['en_attente', 'en_cours'].includes(stepDetail.statutCourant) && (
                                <>
                                  <button className="btn-root" style={{ borderColor: '#2E7D32', color: '#2E7D32' }}
                                    onClick={async () => {
                                      await demarcheAPI.updateStatus(step.id, { statutCourant: 'valide' });
                                      toggleExpand(step.id); fetchSteps(); toast('success', 'Démarche validée');
                                    }}>
                                    <FiCheckCircle className="mr-1" /> Valider
                                  </button>
                                  <button className="btn-root" style={{ borderColor: '#C62828', color: '#C62828' }}
                                    onClick={async () => {
                                      const c = await promptReason('Motif du rejet');
                                      if (c) { await demarcheAPI.updateStatus(step.id, { statutCourant: 'rejete', commentaire: c }); toggleExpand(step.id); fetchSteps(); toast('success', 'Démarche rejetée'); }
                                    }}>
                                    <FiXCircle className="mr-1" /> Rejeter
                                  </button>
                                </>
                              )}
                              {stepDetail.statutCourant === 'en_attente' && isServiceOrAdmin && (
                                <button className="btn-root" style={{ borderColor: '#1565C0', color: '#1565C0' }}
                                  onClick={async () => {
                                    await demarcheAPI.updateStatus(step.id, { statutCourant: 'en_cours' });
                                    toggleExpand(step.id); fetchSteps(); toast('success', 'Démarche en cours');
                                  }}>
                                  <FiClock className="mr-1" /> Marquer en cours
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Timeline */}
                        <div>
                          <h4 className="text-[11px] font-bold tracking-widest uppercase mb-3 flex items-center gap-2" style={{ color: '#7A828A' }}>
                            <FiCalendar className="w-4 h-4" /> Historique
                          </h4>
                          <div className="space-y-0">
                            {(stepDetail.history || []).map((h, i) => {
                              const hCfg = statusConfig[h.statut] || statusConfig.en_attente;
                              const isLast = i === (stepDetail.history || []).length - 1;
                              return (
                                <div key={h.id} className="flex gap-4">
                                  <div className="flex flex-col items-center">
                                    <div className="w-3 h-3 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: hCfg.color }} />
                                    {!isLast && <div className="w-0.5 flex-1" style={{ backgroundColor: '#E5E5E0' }} />}
                                  </div>
                                  <div className={`pb-4 ${isLast ? '' : ''}`}>
                                    <div className="flex items-center gap-2">
                                      <span className="text-[11px] font-bold tracking-widest uppercase"
                                        style={{ color: hCfg.color }}>{hCfg.label}</span>
                                      <span className="text-xs" style={{ color: '#7A828A' }}>
                                        {new Date(h.dateModification).toLocaleString()}
                                      </span>
                                    </div>
                                    {h.commentaire && (
                                      <p className="text-sm mt-1" style={{ color: '#5A626A' }}>{h.commentaire}</p>
                                    )}
                                    {h.modifieParNom && (
                                      <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: '#7A828A' }}>
                                        <FiUser className="w-3 h-3" /> Par {h.modifieParNom}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
